<template>
  <v-dialog
    v-model="showStepsModal"
    width="1050px"
    scrollable
    :persistent="!allStepsDone || hasEventConfirmationSteps"
  >
    <v-card height="600px">
      <v-card-text class="pa-0">
        <steper
          v-model="currentStepValue"
          :steps="steps"
          :finished="allStepsDone"
          hide-back-btn
          @stepSelected="markAsVisited"
          @onFinish="finishSteps"
          @onStepChange="handleStepChange"
        >
          <div v-if="currentStep" class="modal_step_size">
            <v-row justify="center" no-gutters>
              <div class="w-auto mb-3">
                <iusa-logo no-link />
              </div>
            </v-row>
            <div v-if="currentStep.key === 'billingModuleStatus'">
              <billing-option-step />
            </div>
            <div v-if="currentStep.key === 'notifications'">
              <notification-step />
            </div>
            <div v-else-if="currentStep.key === 'reminders'">
              <reminder-step @close-modal="showStepsModal = false" />
            </div>
            <div v-else-if="currentStep.key === 'recentOrders'">
              <recent-orders-step
                @onSelectItem="showStepsModal = false"
                @closeModal="showStepsModal = false"
              />
            </div>
            <div v-else-if="currentStep.key === 'inactivity'">
              <inactivity-step />
            </div>
            <div v-else-if="currentStep.key === 'updateData'">
              <data-update-step />
            </div>
            <div v-else-if="currentStep.key === 'updatePassword'">
              <update-password-step />
            </div>
            <div v-else-if="currentStep.key === 'shoppingCartAlerts'">
              <shopping-cart-alerts-step />
            </div>
            <div v-else-if="currentStep.key === 'notificationPreview'">
              <notification-preview-step @closeModal="showStepsModal = false" />
            </div>
            <div
              v-else-if="
                currentStep.key.startsWith('scheduleEventConfirmation')
              "
            >
              <schedule-event-confirmation-step
                :key="currentStep.key"
                :target-event="currentStep.event"
                @onSubmit="handleScheduleEventSubmit(currentStep.key)"
              />
            </div>
          </div>
        </steper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  import Steper from '@/components/steper/Steper.vue';
  import ReminderStep from './ReminderStep.vue';
  import NotificationStep from './NotificationStep.vue';
  import InactivityStep from './InactivityStep.vue';
  import DataUpdateStep from './DataUpdateStep.vue';
  import UpdatePasswordStep from './UpdatePasswordStep.vue';
  import ShoppingCartAlertsStep from './ShoppingCartAlertsStep.vue';
  import RecentOrdersStep from './RecentOrdersStep.vue';
  import IusaLogo from '@/components/IusaLogo.vue';
  import BillingOptionStep from './BillingOptionStep.vue';
  import NotificationPreviewStep from './NotificationPreviewStep.vue';
  import ScheduleEventConfirmationStep from './ScheduleEventConfirmationStep.vue';

  import { mapGetters, mapActions } from 'vuex';
  import { eventBus } from '@/main.js';

  export default {
    name: 'ModalStepsAfterLogin',
    components: {
      Steper,
      ReminderStep,
      NotificationStep,
      InactivityStep,
      DataUpdateStep,
      UpdatePasswordStep,
      ShoppingCartAlertsStep,
      RecentOrdersStep,
      IusaLogo,
      BillingOptionStep,
      NotificationPreviewStep,
      ScheduleEventConfirmationStep,
    },
    computed: {
      ...mapGetters('modalStepsAfterLogin', [
        'showModal',
        'steps',
        'currentStepVal',
        'inactivityTime',
      ]),
      showStepsModal: {
        set(newValue) {
          this.updateModalState(newValue);
        },
        get() {
          return this.showModal;
        },
      },
      currentStepValue: {
        set(newValue) {
          this.changeCurrentStepValue(newValue);
        },
        get() {
          return this.currentStepVal;
        },
      },
      currentStep() {
        return (
          this.steps.find((s) => s.value === this.currentStepValue) || null
        );
      },
      currentStepDone() {
        return this.currentStep?.done || false;
      },
      allStepsDone() {
        const anyStepUndone = this.steps.find(
          (s) => s.doneRequired !== false && s.done !== true
        );
        return anyStepUndone === undefined;
      },
      hasEventConfirmationSteps() {
        return this.steps.some((s) =>
          s.key.startsWith('scheduleEventConfirmation')
        );
      },
    },
    created() {
      eventBus.$on('scheduleRemoveEventConfirmationStep', (eventId) => {
        this.removeStep(`scheduleEventConfirmation-${eventId}`);
      });
    },
    beforeDestroy() {
      eventBus.$off('scheduleRemoveEventConfirmationStep');
    },
    methods: {
      ...mapActions('modalStepsAfterLogin', [
        'updateModalState',
        'updateStepStatus',
        'findStepByKey',
        'changeCurrentStepValue',
        'removeStep',
      ]),
      ...mapActions('userNotification', ['markNotificationsAsViewed']), // map
      ...mapActions('scheduleNotification', ['dismissConfirmation']),
      markAsVisited(index) {
        const step = this.steps[index];
        step.visited = true;
        this.updateStepStatus(step);
      },
      async finishSteps() {
        try {
          const shouldMarkAsRead =
            this.$store.getters['userNotification/dontShowAgain'];
          if (shouldMarkAsRead) {
            await this.markNotificationsAsViewed();
          }
          this.$store.commit('userNotification/SET_DONT_SHOW_AGAIN', false);
          this.showStepsModal = false;
        } catch (error) {
          this.showStepsModal = false;
        }
      },
      async handleStepChange(step) {
        if (step?.key?.startsWith('scheduleEventConfirmation')) {
          const s = await this.findStepByKey(step.key);
          this.dismissConfirmation(s.eventId);
        }
      },
      async handleScheduleEventSubmit(stepKey) {
        this.removeStep(stepKey);
      },
    },
  };
</script>

<style scoped>
  * {
    text-align: left;
  }

  .v-dialog {
    max-width: 90vw;
  }
</style>
